import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { authApi } from '@/lib/api'
import type { AuthUser } from '@/lib/api'
import { AppLayout } from '@/layouts/AppLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { RouteFallback } from '@/components/RouteFallback'
import { LoginPage } from '@/pages/LoginPage'

// Retry wrapper for lazy imports — handles stale chunk errors after deploys
function lazyRetry<T extends Record<string, unknown>>(
  factory: () => Promise<T>,
): Promise<T> {
  return factory().catch((err: unknown) => {
    const isChunkError = err instanceof Error &&
      (err.message.includes('dynamically imported module') ||
       err.message.includes('Failed to fetch') ||
       err.message.includes('Loading chunk'))
    if (isChunkError) {
      window.location.reload()
    }
    throw err
  })
}

// Lazy-load all pages for code splitting (with auto-reload on stale chunks)
const DashboardPage = lazy(() => lazyRetry(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage }))))
const AgentsPage = lazy(() => lazyRetry(() => import('@/pages/AgentsPage').then(m => ({ default: m.AgentsPage }))))
const AgentBuilderPage = lazy(() => lazyRetry(() => import('@/pages/AgentBuilderPage').then(m => ({ default: m.AgentBuilderPage }))))
const WorkflowsPage = lazy(() => lazyRetry(() => import('@/pages/WorkflowsPage').then(m => ({ default: m.WorkflowsPage }))))
const WorkflowBuilderPage = lazy(() => lazyRetry(() => import('@/pages/WorkflowBuilderPage').then(m => ({ default: m.WorkflowBuilderPage }))))
const PlaygroundPage = lazy(() => lazyRetry(() => import('@/pages/PlaygroundPage').then(m => ({ default: m.PlaygroundPage }))))
const CallHistoryPage = lazy(() => lazyRetry(() => import('@/pages/CallHistoryPage').then(m => ({ default: m.CallHistoryPage }))))
const CallDetailPage = lazy(() => lazyRetry(() => import('@/pages/CallDetailPage').then(m => ({ default: m.CallDetailPage }))))
const PhoneNumbersPage = lazy(() => lazyRetry(() => import('@/pages/PhoneNumbersPage').then(m => ({ default: m.PhoneNumbersPage }))))
const SipTrunksPage = lazy(() => lazyRetry(() => import('@/pages/SipTrunksPage').then(m => ({ default: m.SipTrunksPage }))))
const KnowledgeBasesPage = lazy(() => lazyRetry(() => import('@/pages/KnowledgeBasesPage').then(m => ({ default: m.KnowledgeBasesPage }))))
const KnowledgeBaseDetailPage = lazy(() => lazyRetry(() => import('@/pages/KnowledgeBaseDetailPage')))
const CampaignsPage = lazy(() => lazyRetry(() => import('@/pages/CampaignsPage').then(m => ({ default: m.CampaignsPage }))))
const AbTestsPage = lazy(() => lazyRetry(() => import('@/pages/AbTestsPage').then(m => ({ default: m.AbTestsPage }))))
const UsagePage = lazy(() => lazyRetry(() => import('@/pages/UsagePage').then(m => ({ default: m.UsagePage }))))
const ToolsPage = lazy(() => lazyRetry(() => import('@/pages/ToolsPage').then(m => ({ default: m.ToolsPage }))))
const TransferDirectoryPage = lazy(() => lazyRetry(() => import('@/pages/TransferDirectoryPage').then(m => ({ default: m.TransferDirectoryPage }))))
const ApiKeysPage = lazy(() => lazyRetry(() => import('@/pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage }))))
const WebhooksPage = lazy(() => lazyRetry(() => import('@/pages/WebhooksPage').then(m => ({ default: m.WebhooksPage }))))
const SettingsPage = lazy(() => lazyRetry(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage }))))
const WorkspaceSettingsPage = lazy(() => lazyRetry(() => import('@/pages/WorkspaceSettingsPage').then(m => ({ default: m.WorkspaceSettingsPage }))))
const ProfilePage = lazy(() => lazyRetry(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage }))))
const VoicesPage = lazy(() => lazyRetry(() => import('@/pages/VoicesPage').then(m => ({ default: m.VoicesPage }))))
const TestSuitesPage = lazy(() => lazyRetry(() => import('@/pages/TestSuitesPage').then(m => ({ default: m.TestSuitesPage }))))
const TestSuiteDetailPage = lazy(() => lazyRetry(() => import('@/pages/TestSuiteDetailPage')))
const ReviewQueuePage = lazy(() => lazyRetry(() => import('@/pages/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage }))))
const CallDebuggerPage = lazy(() => lazyRetry(() => import('@/pages/CallDebuggerPage')))
const LatencyTrackerPage = lazy(() => lazyRetry(() => import('@/pages/LatencyTrackerPage').then(m => ({ default: m.LatencyTrackerPage }))))
const BenchmarksPage = lazy(() => lazyRetry(() => import('@/pages/BenchmarksPage').then(m => ({ default: m.BenchmarksPage }))))
const HeartbeatAgentsPage = lazy(() => lazyRetry(() => import('@/pages/HeartbeatAgentsPage').then(m => ({ default: m.HeartbeatAgentsPage }))))
const IntegrationsPage = lazy(() => lazyRetry(() => import('@/pages/IntegrationsPage').then(m => ({ default: m.IntegrationsPage }))))
const PlatformDashboardPage = lazy(() => lazyRetry(() => import('@/pages/PlatformDashboardPage').then(m => ({ default: m.PlatformDashboardPage }))))
const MagicLinkVerifyPage = lazy(() => lazyRetry(() => import('@/pages/MagicLinkVerifyPage')))
const AcceptInvitePage = lazy(() => lazyRetry(() => import('@/pages/AcceptInvitePage')))
const OnboardingPage = lazy(() => lazyRetry(() => import('@/pages/OnboardingPage')))
const ForgotPasswordPage = lazy(() => lazyRetry(() => import('@/pages/ForgotPasswordPage')))
const ResetPasswordPage = lazy(() => lazyRetry(() => import('@/pages/ResetPasswordPage')))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

/** Routes that bypass auth (accept-invite, magic link verify, password reset) */
const PUBLIC_PATHS = ['/accept-invite', '/auth/magic', '/auth/forgot-password', '/auth/reset-password']

function SessionCheck({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, setSession, setLoading } = useAuthStore()
  const location = useLocation()

  // Allow public paths without auth
  const isPublicPath = PUBLIC_PATHS.some((p) => location.pathname.startsWith(p))

  useEffect(() => {
    if (isPublicPath) {
      setLoading(false)
      return
    }

    // If already authenticated via legacy API key, skip session check
    if (localStorage.getItem('relay_api_key')) {
      setLoading(false)
      return
    }

    // Try to restore session from cookie
    authApi.me()
      .then(({ user, orgs, activeOrg }) => {
        setSession(user, orgs, activeOrg)
        // Apply server-side theme preference (default light for new users)
        const pref = (user.themePreference as 'light' | 'dark') || 'light'
        useThemeStore.getState().setThemeFromServer(pref)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setSession, setLoading, isPublicPath])

  if (isPublicPath) return <>{children}</>

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-base">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          <p className="text-sm text-text-muted">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  return <>{children}</>
}

/** Redirect to onboarding if user hasn't completed it */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const { onboardingCompleted, apiKey } = useAuthStore()
  const location = useLocation()

  // Skip onboarding check for API key users or if already on onboarding page
  if (apiKey || onboardingCompleted || location.pathname === '/onboarding') {
    return <>{children}</>
  }

  return <Navigate to="/onboarding" replace />
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

/** Redirect /compositions/:id → /workflows/:id/edit */
function CompositionsRedirect() {
  const { id } = useParams()
  return <Navigate to={`/workflows/${id}/edit`} replace />
}

export default function App() {
  const { theme } = useThemeStore()

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
  }, [theme])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <SessionCheck>
          <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes (no auth required) */}
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/auth/magic" element={<MagicLinkVerifyPage />} />
                <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

                {/* Onboarding (auth required, no layout) */}
                <Route path="/onboarding" element={<OnboardingPage />} />

                {/* Authenticated app routes */}
                <Route element={<OnboardingGuard><AppLayout /></OnboardingGuard>}>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/agents" element={<AgentsPage />} />
                  <Route path="/agents/new" element={<AgentBuilderPage />} />
                  <Route path="/agents/:id" element={<AgentBuilderPage />} />
                  <Route path="/workflows" element={<WorkflowsPage />} />
                  <Route path="/workflows/new" element={<WorkflowBuilderPage />} />
                  <Route path="/workflows/:id/edit" element={<WorkflowBuilderPage />} />
                  {/* Backward-compat redirects */}
                  <Route path="/compositions" element={<Navigate to="/workflows" replace />} />
                  <Route path="/compositions/new" element={<Navigate to="/workflows/new" replace />} />
                  <Route path="/compositions/:id" element={<CompositionsRedirect />} />
                  <Route path="/playground" element={<PlaygroundPage />} />
                  <Route path="/calls" element={<CallHistoryPage />} />
                  <Route path="/calls/:id" element={<CallDetailPage />} />
                  <Route path="/calls/:id/debug" element={<CallDebuggerPage />} />
                  <Route path="/reviews" element={<ReviewQueuePage />} />
                  <Route path="/test-suites" element={<TestSuitesPage />} />
                  <Route path="/test-suites/:id" element={<TestSuiteDetailPage />} />
                  <Route path="/phone-numbers" element={<PhoneNumbersPage />} />
                  <Route path="/sip-trunks" element={<SipTrunksPage />} />
                  <Route path="/knowledge-bases" element={<KnowledgeBasesPage />} />
                  <Route path="/knowledge-bases/:id" element={<KnowledgeBaseDetailPage />} />
                  <Route path="/campaigns" element={<CampaignsPage />} />
                  <Route path="/ab-tests" element={<AbTestsPage />} />
                  <Route path="/usage" element={<UsagePage />} />
                  <Route path="/tools" element={<ToolsPage />} />
                  <Route path="/transfer-directory" element={<TransferDirectoryPage />} />
                  <Route path="/api-keys" element={<ApiKeysPage />} />
                  <Route path="/webhooks" element={<WebhooksPage />} />
                  <Route path="/voices" element={<VoicesPage />} />
                  <Route path="/latency" element={<LatencyTrackerPage />} />
                  <Route path="/benchmarks" element={<BenchmarksPage />} />
                  <Route path="/heartbeat-agents" element={<HeartbeatAgentsPage />} />
                  <Route path="/platform" element={<PlatformDashboardPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/integrations" element={<IntegrationsPage />} />
                  <Route path="/settings/workspace" element={<WorkspaceSettingsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                </Route>
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </SessionCheck>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
